import React from 'react';
import { Column, Row } from 'react-table';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import { useSelector } from 'react-redux';
import {
    getUser,
    shouldShowManageTenants,
    shouldShowMerchants,
    shouldShowScheduledDeployment,
    shouldShowTenantGroups
} from 'Selectors';
import useStickyTableHead from 'Hooks/useStickyTableHead';
import Table from 'Components/Table';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';

type ReactTableColumn = Column<DeploymentHistoryData>;

type FilteredColumns = ReactTableColumn[];
type AllColumns = Array<ReactTableColumn & { isHidden?: boolean }>;

type Props = {
    deploymentHistory: DeploymentHistoryData[] | undefined;
    isLoading: boolean;
};

const TABLEID = 'revisions-past-deployments-table';

const DeploymentHistoryTable = ({ deploymentHistory, isLoading }: Props) => {
    const { t } = useTranslation();
    useStickyTableHead(TABLEID);
    const user = useSelector(getUser);

    const windowParams = [
        'top=0',
        'left=0',
        `height=${window?.screen?.height}`,
        `width=${window?.screen?.width}`,
        'fullscreen=yes'
    ].join(',');
    const openWindow = (id: number) => {
        const windowUrl = `/revisions/${id}`;
        window?.open(windowUrl, windowUrl, windowParams);
    };
    const deploymentsDataPatched = cloneDeep(deploymentHistory);
    const handleRowClick = (row: Row<DeploymentHistoryData>) => {
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Manage Revisions - Open Deployed Version from Revisions',
                text: 'Open Deployed from Revisions'
            }
        });
        openWindow(row?.values?.version);
    };

    const allColumns: AllColumns = [
        {
            Header: '',
            id: 'modify-scheduled-deployment',
            disableSortBy: true,
            width: 50,
            maxWidth: 50,
            renderType: 'overrideCell',
            Cell: () => {
                return null;
            },
            isHidden: !shouldShowScheduledDeployment()
        },
        {
            Header: 'Version ID',
            accessor: 'version',
            id: 'version',
            renderType: 'link',
            renderOptions: {
                to: 'revisions/$0',
                openNewWindow: true,
                newWindowParams: windowParams,
                iconName: 'expand'
            },
            style: {
                width: '8%'
            },
            disableSortBy: true,
            dataType: 'number'
        },
        {
            Header: 'Status',
            accessor: () => t('Success'),
            id: 'status',
            disableSortBy: true,
            dataType: 'text',
            style: {
                width: '8%'
            },
            isHidden: !shouldShowScheduledDeployment()
        },
        {
            Header: 'Comment',
            accessor: 'comment',
            id: 'comment',
            style: {
                // accounting for the width of the actionRow
                maxWidth: 'calc(20% - 40px)'
            },
            dataType: 'text'
        },
        {
            Header: 'Deployed',
            accessor: 'createdAt',
            id: '_formatted.createdAt',
            preformattedValue: (d) => d.createdAt,
            style: {
                width: '10%'
            },
            dataType: 'datetime',
            renderType: 'datetime',
            sortType: 'datetime'
        },
        {
            Header: 'Deployed By',
            accessor: (d) => d._formatted?.createdBy,
            id: '_formatted.createdBy',
            style: {
                width: '10%'
            },
            dataType: 'text'
        },
        {
            Header: 'New Global Rules',
            accessor: 'addedRules',
            id: 'newGlobalRules',
            style: {
                width: '8%'
            },
            dataType: 'number'
        },
        {
            Header: 'Modified Global Rules',
            accessor: 'modifiedGlobalRules',
            id: 'modifiedGlobalRules',
            style: {
                width: '9%'
            },
            dataType: 'number'
        },
        {
            Header: t('New Merchants'),
            accessor: 'newMerchants',
            id: 'newMerchants',
            style: {
                width: '7%'
            },
            dataType: 'number',
            isHidden: !shouldShowMerchants()
        },
        {
            Header: t('Modified Merchants'),
            accessor: 'modifiedMerchants',
            id: 'modifiedMerchants',
            style: {
                width: '7%'
            },
            dataType: 'number',
            isHidden: !shouldShowMerchants()
        },
        {
            Header: t('Deactivated Merchants'),
            accessor: 'deactivatedMerchants',
            id: 'deactivatedMerchants',
            style: {
                width: '8%'
            },
            dataType: 'number',
            isHidden: !shouldShowMerchants()
        },
        {
            Header: t('New Tenant Groups'),
            accessor: 'newTenantGroups',
            id: 'newTenantGroups',
            style: {
                width: '7%'
            },
            dataType: 'number',
            isHidden: !shouldShowManageTenants(user?.landlordId, user?.tenantKey) && shouldShowTenantGroups()
        },
        {
            Header: t('Modified Tenant Groups'),
            accessor: 'modifiedTenantGroups',
            id: 'modifiedTenantGroups',
            style: {
                width: '7%'
            },
            dataType: 'number',
            isHidden: !shouldShowManageTenants(user?.landlordId, user?.tenantKey) && shouldShowTenantGroups()
        }
    ];

    const filteredColumns = allColumns.reduce((columns: FilteredColumns, column) => {
        if (!column.isHidden) {
            const { isHidden, ...resultColumn } = column;
            columns.push(resultColumn);
        }
        return columns;
    }, []);

    deploymentsDataPatched?.forEach((revision) => {
        revision.modifiedTenantGroups = (revision?.modifiedTenantGroups ?? 0) + (revision?.removedTenantGroups ?? 0);
    });

    return (
        <Table
            tableName='deployment-history-table'
            showRichFilter
            showMultiSort
            showExpandableSearch
            enablePagination
            initialState={{}}
            dataTestId={TABLEID}
            columns={filteredColumns}
            data={deploymentsDataPatched || []}
            clickableRow
            onRowInteractCB={handleRowClick}
            innerTableHeader={t('Deployment History')}
            isLoading={isLoading}
        />
    );
};

export default React.memo(DeploymentHistoryTable);
